import { useEffect, useMemo, useState } from 'react';
import { Rocket } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import type { Address } from 'viem';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { ERC20_ABI, LAUNCHPAD_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

const toNum = (v: bigint) => Number(formatUnits(v, 18));

export default function LaunchpadView() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const launchpad = deployment?.launchpad;

  const { data: saleCount } = useReadContract({
    address: launchpad?.address,
    abi: LAUNCHPAD_ABI,
    functionName: 'saleCount',
    chainId: readChainId,
    query: { enabled: Boolean(launchpad) },
  });

  if (!launchpad) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Launchpad is not deployed on this network.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Rocket size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Launchpad</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            신규 프로젝트 토큰을 고정가로 판매하는 공개 세일 — 전량 온체인 에스크로
          </p>
        </div>
      </div>

      {saleCount === undefined ? (
        <div className="text-xs text-foam-500 py-8 text-center">세일 정보를 불러오는 중...</div>
      ) : Number(saleCount) === 0 ? (
        <div className="text-xs text-foam-500 py-8 text-center">진행 중인 세일이 없습니다.</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: Number(saleCount) }, (_, i) => (
            <SaleCard key={i} saleId={i} launchpadAddress={launchpad.address} />
          ))}
        </div>
      )}

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        판매 물량은 세일 생성 시점에 전량 컨트랙트에 예치(에스크로)되며, 구매 즉시 토큰이 지갑으로
        전송됩니다. 판매 대금은 컨트랙트에 쌓이지 않고 매 구매마다 수령 지갑으로 직접 이동합니다. (BSC
        테스트넷 데모 — OCEA는 가상의 예시 프로젝트입니다)
      </div>
      <TrustBar contract={launchpad.address} />
    </div>
  );
}

function SaleCard({ saleId, launchpadAddress }: { saleId: number; launchpadAddress: Address }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;

  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);
  const amountWei = safeParse(amount);

  const { data: sale, refetch: refetchSale } = useReadContract({
    address: launchpadAddress,
    abi: LAUNCHPAD_ABI,
    functionName: 'sales',
    args: [BigInt(saleId)],
    chainId: readChainId,
    query: { refetchInterval: 15000 },
  });

  const saleToken = sale?.[0];
  const payToken = sale?.[1];
  const price = sale?.[2];
  const cap = sale?.[3];
  const sold = sale?.[4];
  const userCap = sale?.[5];
  const startTime = sale?.[6];
  const endTime = sale?.[7];

  const { data: saleSymbol } = useReadContract({
    address: saleToken,
    abi: ERC20_ABI,
    functionName: 'symbol',
    chainId: readChainId,
    query: { enabled: Boolean(saleToken) },
  });

  const { data: paySymbol } = useReadContract({
    address: payToken,
    abi: ERC20_ABI,
    functionName: 'symbol',
    chainId: readChainId,
    query: { enabled: Boolean(payToken) },
  });

  const { data: myPurchased, refetch: refetchPurchased } = useReadContract({
    address: launchpadAddress,
    abi: LAUNCHPAD_ABI,
    functionName: 'purchased',
    args: address ? [BigInt(saleId), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address) },
  });

  const { data: payBalance, refetch: refetchPayBalance } = useReadContract({
    address: payToken,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(payToken && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: payToken,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, launchpadAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(payToken && address) },
  });

  const cost = amountWei !== null && price !== undefined ? (amountWei * price) / 10n ** 18n : null;
  const needsApproval = cost !== null && (allowance === undefined || allowance < cost);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract: writeBuy, data: buyHash, isPending: isBuyPending, reset: resetBuy } = useWriteContract();
  const { isLoading: isBuyConfirming, isSuccess: isBuyConfirmed, error: buyReceiptError, refetch: retryBuyReceipt } = useWaitForTransactionReceipt({ hash: buyHash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isBuyConfirmed && buyHash && address) {
      logTransaction({
        type: 'launchpadBuy',
        hash: buyHash,
        account: address,
        chainId: readChainId,
        details: { pool: `IDO-${saleSymbol ?? saleId}`, amount },
      });
      setAmount('');
      refetchSale();
      refetchPurchased();
      refetchPayBalance();
      resetBuy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuyConfirmed]);

  const status = useMemo(() => {
    if (startTime === undefined || endTime === undefined) return undefined;
    const now = Date.now() / 1000;
    if (now < Number(startTime)) return 'upcoming' as const;
    if (now >= Number(endTime)) return 'ended' as const;
    if (cap !== undefined && sold !== undefined && sold >= cap) return 'soldout' as const;
    return 'live' as const;
  }, [startTime, endTime, cap, sold]);

  const handleApprove = () => {
    if (!payToken) return;
    setTxError(null);
    writeApprove(
      { address: payToken, abi: ERC20_ABI, functionName: 'approve', args: [launchpadAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleBuy = () => {
    if (!amountWei) return;
    setTxError(null);
    writeBuy(
      { address: launchpadAddress, abi: LAUNCHPAD_ABI, functionName: 'buy', args: [BigInt(saleId), amountWei] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const isPending = isApprovePending || isApproveConfirming || isBuyPending || isBuyConfirming;
  const progress = cap && cap > 0n && sold !== undefined ? (toNum(sold) / toNum(cap)) * 100 : 0;

  const remainingUserCap =
    userCap !== undefined && userCap > 0n && myPurchased !== undefined ? userCap - myPurchased : undefined;
  const overUserCap = remainingUserCap !== undefined && amountWei !== null && amountWei > remainingUserCap;
  const insufficientPay = cost !== null && payBalance !== undefined && payBalance < cost;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (status !== 'live') {
    buttonLabel = status === 'upcoming' ? '세일 시작 전' : status === 'soldout' ? '완판' : '세일 종료';
  } else if (amountWei) {
    if (overUserCap) {
      buttonLabel = '1인 한도 초과';
    } else if (insufficientPay) {
      buttonLabel = `${paySymbol ?? ''} 잔액 부족`;
    } else if (isApprovePending || isApproveConfirming) {
      buttonLabel = 'Approving...';
    } else if (needsApproval) {
      buttonLabel = `Approve ${paySymbol ?? ''}`;
      buttonAction = handleApprove;
      buttonDisabled = false;
    } else if (isBuyPending || isBuyConfirming) {
      buttonLabel = '구매 중...';
    } else {
      buttonLabel = '구매';
      buttonAction = handleBuy;
      buttonDisabled = false;
    }
  }

  const statusMeta = {
    live: { label: 'LIVE', cls: 'text-tide-400 bg-tide-400/10' },
    upcoming: { label: '예정', cls: 'text-sky-400 bg-sky-400/10' },
    ended: { label: '종료', cls: 'text-foam-500 bg-abyss-700/60' },
    soldout: { label: '완판', cls: 'text-coral-400 bg-coral-500/10' },
  } as const;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <TokenIcon symbol={saleSymbol ?? '?'} size={32} />
          <div>
            <div className="text-sm font-semibold text-foam-100">{saleSymbol ?? '...'} 공개 세일</div>
            <div className="text-[11px] text-foam-500 tabular-nums">
              1 {saleSymbol ?? ''} = {price !== undefined ? toNum(price).toLocaleString('en-US', { maximumFractionDigits: 6 }) : '-'}{' '}
              {paySymbol ?? ''}
            </div>
          </div>
        </div>
        {status && (
          <span className={`text-[11px] font-semibold px-2 py-1 rounded-md ${statusMeta[status].cls}`}>
            {statusMeta[status].label}
          </span>
        )}
      </div>

      <div>
        <div className="flex justify-between text-[11px] text-foam-500 mb-1">
          <span>
            판매 진행 {sold !== undefined ? toNum(sold).toLocaleString('en-US') : '-'} /{' '}
            {cap !== undefined ? toNum(cap).toLocaleString('en-US') : '-'} {saleSymbol ?? ''}
          </span>
          <span className="tabular-nums">{progress.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-abyss-900 overflow-hidden">
          <div className="h-full bg-tide-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500">종료일</div>
          <div className="text-foam-100 font-medium mt-0.5">
            {endTime !== undefined ? new Date(Number(endTime) * 1000).toLocaleDateString('ko-KR') : '-'}
          </div>
        </div>
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500">1인 한도</div>
          <div className="text-foam-100 font-medium mt-0.5 tabular-nums">
            {userCap !== undefined && userCap > 0n ? toNum(userCap).toLocaleString('en-US') : '무제한'}
          </div>
        </div>
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500">내 구매량</div>
          <div className="text-tide-400 font-medium mt-0.5 tabular-nums">
            {myPurchased !== undefined ? toNum(myPurchased).toLocaleString('en-US') : '-'}
          </div>
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>구매 수량 ({saleSymbol ?? ''})</span>
          <span>
            {paySymbol ?? ''} 잔액: {payBalance !== undefined ? toNum(payBalance).toFixed(2) : '-'}
          </span>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
        {cost !== null && (
          <div className="text-[11px] text-foam-500 mt-1">
            비용: <span className="text-foam-300 tabular-nums">{toNum(cost).toLocaleString('en-US', { maximumFractionDigits: 4 })} {paySymbol ?? ''}</span>
          </div>
        )}
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || isPending}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {buttonLabel}
      </button>

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: buyReceiptError, hash: buyHash, retry: retryBuyReceipt },
      ]} />
      {isBuyConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
          구매 완료 — 토큰이 지갑으로 전송되었습니다.
        </div>
      )}
    </div>
  );
}
